// shadcn/ui tabs (CLI 4.21.0), rebound to docs/tokens.css. Radix gives the
// roving focus and the arrow keys; the look is ours.
//
// UI-8: every tab row is a segmented control — a gray track, the chosen
// segment a raised thumb with its soft shadow, a 180ms fade between them.
// Two variants, which differ only in how they hold many items:
//
//   line      — the status tabs over a list, a row of countries: the track
//               scrolls sideways when it runs out of room.
//   segmented — a small switch of two or three choices: desktop / mobile.
import * as React from "react";
import { Tabs as TabsPrimitive } from "radix-ui";
import { cn } from "@/lib/utils";

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={cn("flex flex-col gap-3", className)} {...props} />;
}

function TabsList({
  className,
  variant = "line",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & { variant?: "line" | "segmented" }) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(
        "group/tabs-list inline-flex w-fit max-w-full items-center gap-0.5 rounded-inner bg-fill p-0.5",
        variant === "line" && "overflow-x-auto",
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "inline-flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-control px-3 text-detail font-medium whitespace-nowrap text-foreground transition-[background-color,box-shadow,color] hover:bg-thumb/50 disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=active]:bg-thumb data-[state=active]:font-semibold data-[state=active]:shadow-thumb data-[state=active]:hover:bg-thumb",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content data-slot="tabs-content" className={cn("outline-none", className)} {...props} />
  );
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
